// pkg99 — referral card renders the eligibility line (D-99c): when referrer_eligible is false (cardless trial / past_due), an honest amber note says rewards require an active subscription — BEFORE anyone spends effort referring — delivered 2026-07-18
// pkg95 — larger pack purchases + shortfall handoff (flat $1/credit, Ryan's call — no volume discounts, the ≥70% margin math assumes full price): QUICK-PICK preset buttons (5 / 20 / 50 credits — one click sets the pack stepper; the stepper stays for fine-tuning; same single Checkout charge, same webhook, no new Stripe products) and a ?need=N query param from the Discovery Analysis shortfall flow that pre-selects the smallest pack count covering the shortfall and banners why — delivered 2026-07-17
// pkg71 — "Refer a firm" card (D-71d: code = the firm's subdomain, copy-link, give/get amounts from the server, referral list with vested/pending status) + EXEMPT WALLET REWORK (D-71e: exempt firms now get the full wallet — complimentary banner, dormant-but-visible balance, referral card, history — buy-stepper hidden) + ledger label map gains referral/adjustment (D-71f) — delivered 2026-07-16
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg68 — Firm Wallet page (SETTINGS): the credit wallet in one place — prominent balance + free-actions counter, in-portal pack stepper (1–20, live math, exact quantity to Checkout), and the full transaction history from the credit ledger; receives ?billing=credits-success|cancelled (D-68e) — delivered 2026-07-12
//
// "Firm Wallet" is what the firm has loaded and spent (AI credits). What
// LawMighty charges the firm — subscription, card, receipts — lives next
// door on Firm Billing.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const META = 'Arial, sans-serif';
const MAX_PACKS = 20; // mirrors MAX_PACKS_PER_BUY server-side; server validates

export default function AdminFirmWalletPage() {
  const [billing, setBilling] = useState(null);
  const [ledger, setLedger]   = useState(null);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [qty, setQty]         = useState(1);
  const [referrals, setReferrals] = useState(null); // pkg71 (D-71d) — { code, link, referrer_credits, referee_credits, referrals[] }
  const [copied, setCopied]       = useState(false); // pkg71 — copy-link feedback
  // pkg95 — ?need=N (the Discovery Analysis shortfall handoff): how many
  // credits short the run is. Pre-selects the smallest covering pack count.
  const [needCredits] = useState(() => {
    const n = parseInt(new URLSearchParams(window.location.search).get('need') || '', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });

  const loadAll = useCallback((withLedger = true) => {
    adminApi.getBillingStatus().then(setBilling).catch(e => setErr(e.message));
    if (withLedger) {
      adminApi.getFirmBillingLedger()
        .then(d => setLedger(d.entries || []))
        .catch(() => setLedger([])); // ledger failing shouldn't blank the wallet
    }
    // pkg71 — the referral card is additive: a fetch failure hides the card,
    // never the wallet.
    adminApi.getReferrals().then(setReferrals).catch(() => setReferrals(null));
  }, []);

  // pkg71 — copy the referral link. Clipboard API first; execCommand textarea
  // fallback for older/locked-down browsers. Feedback resets after 2s.
  async function copyReferralLink() {
    if (!referrals) return;
    try {
      await navigator.clipboard.writeText(referrals.link);
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = referrals.link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_e) { return; }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get('billing');
    if (flag === 'credits-success') setMsg('Credits purchased — they can take a few seconds to appear below.');
    if (flag === 'cancelled')       setMsg('Checkout was cancelled — nothing was charged.');
    loadAll();
    // Webhook lag on a purchase return: refresh a few times so the new
    // balance and ledger row appear without a manual reload.
    if (flag === 'credits-success') {
      const t1 = setTimeout(() => loadAll(), 2500);
      const t2 = setTimeout(() => loadAll(), 6000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [loadAll]);

  async function buyCredits() {
    setErr(''); setPayBusy(true);
    try {
      const { url } = await adminApi.createCreditsCheckout(qty);
      window.location.href = url;
    } catch (e) {
      setErr(e.message || 'Could not open checkout.');
      setPayBusy(false);
    }
  }

  const clampQty = (n) => Math.min(MAX_PACKS, Math.max(1, n));
  const packWord = qty === 1 ? 'pack' : 'packs';
  const packCredits = billing?.prices?.packCredits ?? 5;
  const packPriceCents = billing?.prices?.packPriceCents ?? 500;
  const totalCredits = qty * packCredits;
  const totalDollars = ((qty * packPriceCents) / 100).toFixed(2);

  // pkg95 — the shortfall handoff pre-selects the smallest pack count that
  // covers the need. Runs once when billing (and so packCredits) arrives.
  useEffect(() => {
    if (needCredits > 0 && billing && !billing.exempt) {
      setQty(Math.min(MAX_PACKS, Math.max(1, Math.ceil(needCredits / (billing?.prices?.packCredits ?? 5)))));
    }
  }, [needCredits, billing]);

  // pkg95 — quick picks: one click instead of stepping. Same stepper, same
  // Checkout, same webhook — presets just set the quantity.
  const PRESET_PACKS = [1, 4, 10];

  const fmtDay = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  // A ledger row's human line: prefer the stored detail; fall back to a
  // friendly label per reason (pkg71 / D-71f — 'referral' and the pkg87
  // invite 'adjustment' rows now read like sentences instead of codes).
  const REASON_LABELS = { // pkg71 (D-71f)
    referral:    'Referral reward',
    adjustment:  'Credit grant',
    purchase:    'Credits purchased',
    spend:       'Credit used',
    free_action: 'Free action',
  };
  function activityLine(e) {
    if (e.detail) return e.detail;
    if (e.reason) return REASON_LABELS[e.reason] || String(e.reason).replace(/_/g, ' ');
    return e.delta > 0 ? 'Credits added' : 'Credit used';
  }

  return (
    <div>
      <h1 className="page-title">Firm Wallet</h1>
      <p className="page-subtitle">
        Your firm's AI credits — the balance, buying packs, and every transaction. AI actions
        (Document Module builds and AI Discovery drafts) use 1 credit each after your free actions.
        Your subscription and receipts live on <Link to="/admin/dashboard/firm-billing">Firm Billing</Link>.
      </p>

      {msg && <div className="alert alert-success">{msg}</div>}
      {err && <div className="alert alert-error">{err}</div>}

      {!billing && !err && (
        <div className="card"><p style={{ fontFamily: META, fontSize: 13, color: '#888', margin: 0 }}>Loading wallet…</p></div>
      )}

      {/* pkg71 (D-71e) — the exempt view is no longer a dead end: banner on
          top, then the SAME wallet everyone else gets (balance, referral
          card, history) with only the buy-stepper hidden. Earned credits are
          dormant-but-visible: aiCredits.js never consumes for exempt firms,
          so the rows just wait with the account. */}
      {billing && billing.exempt && (
        <div className="card" style={{ marginBottom: 16 }}>
          <p style={{ fontFamily: META, fontSize: 13, color: '#333', margin: 0 }}>
            <strong>Complimentary plan.</strong> AI actions are included for your firm — credits you earn
            (referral rewards) are saved with your account and shown below.
          </p>
        </div>
      )}

      {billing && (
        <>
          {/* Balance */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: META, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {billing.exempt ? 'AI credits (dormant)' : 'AI credits'}
                </div>
                <div style={{ fontSize: 34, fontWeight: 600, lineHeight: 1.2 }}>{billing.ai.creditBalance}</div>
              </div>
              {!billing.exempt && (
                <div>
                  <div style={{ fontFamily: META, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Free actions left</div>
                  <div style={{ fontSize: 34, fontWeight: 600, lineHeight: 1.2 }}>
                    {billing.ai.freeRemaining}<span style={{ fontSize: 16, color: '#888', fontWeight: 400 }}> of {billing.ai.freeTotal}</span>
                  </div>
                </div>
              )}
              {billing.exempt && (
                <div style={{ alignSelf: 'center', maxWidth: 420 }}>
                  <p style={{ fontFamily: META, fontSize: 12, color: '#888', margin: 0 }}>
                    Your plan includes AI actions, so these aren't consumed — they stay with your
                    account if your plan ever changes.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* pkg71 (D-71d) — Refer a firm */}
          {referrals && (
            <>
              <div className="section-head">Refer a firm</div>
              <div className="card" style={{ marginBottom: 16 }}>
                <p style={{ fontFamily: META, fontSize: 13, color: '#333', marginTop: 0, marginBottom: 12 }}>
                  When a firm you refer makes their first payment, your firm gets{' '}
                  <strong>{referrals.referrer_credits} credits</strong> and they get{' '}
                  <strong>{referrals.referee_credits} credits</strong>. No limit on referrals.
                </p>
                {referrals.referrer_eligible === false && (
                  /* pkg99 (D-99c) — mirrors the webhook's vest-time test; strict
                     === false so older cached payloads without the flag change
                     nothing. The link still works and the joining firm still
                     gets their credits — only the referrer reward is gated. */
                  <p style={{ fontFamily: META, fontSize: 13, color: '#7c5200', background: '#fff8e1',
                    border: '1px solid #f5c842', borderRadius: 6, padding: '8px 12px', marginTop: 0, marginBottom: 12 }}>
                    Referral rewards require an active subscription. Firms you refer still get their
                    welcome credits, but your firm's reward is only granted while your subscription
                    is active when their first payment lands.
                  </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <code style={{ fontSize: 13, background: '#F5F1E8', border: '1px solid #ddd', borderRadius: 6, padding: '7px 12px' }}>
                    {referrals.link}
                  </code>
                  <button type="button" className="btn btn-primary" onClick={copyReferralLink}>
                    {copied ? '✓ Copied' : 'Copy link'}
                  </button>
                </div>
                <p style={{ fontFamily: META, fontSize: 12, color: '#888', marginTop: 0, marginBottom: referrals.referrals.length ? 12 : 0 }}>
                  Your referral code is your subdomain — <code>{referrals.code}</code> — a firm can also
                  type it on the signup form.
                </p>
                {referrals.referrals.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: META, fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#888' }}>
                        <th style={{ padding: '4px 8px 8px 0', fontWeight: 600 }}>Firm</th>
                        <th style={{ padding: '4px 8px 8px 0', fontWeight: 600 }}>Joined</th>
                        <th style={{ padding: '4px 0 8px 0', fontWeight: 600, textAlign: 'right' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.referrals.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '8px 8px 8px 0' }}>{r.firm_name || '—'}</td>
                          <td style={{ padding: '8px 8px 8px 0', color: '#555', whiteSpace: 'nowrap' }}>{fmtDay(r.created_at)}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right' }}>
                            {r.rewarded ? (
                              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: '#eaf5ee', color: '#1d7a4f' }}>
                                +{referrals.referrer_credits} credits earned
                              </span>
                            ) : (
                              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: '#F5F1E8', color: '#8a6d3b' }}>
                                Pending first payment
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Buy packs — hidden for exempt firms (D-71e): nothing to buy */}
          {!billing.exempt && (
          <>
          <div className="section-head">Buy credits</div>
          <div className="card" style={{ marginBottom: 16 }}>
            {!billing.subscribed ? (
              <p style={{ fontFamily: META, fontSize: 13, color: '#333', margin: 0 }}>
                An active LawMighty subscription is required to buy AI credits.{' '}
                <Link to="/admin/dashboard/firm-billing">Subscribe on Firm Billing</Link> ($29.99/month), then load
                your wallet here.
              </p>
            ) : (
              <>
                {/* pkg95 — shortfall handoff banner: the Discovery Analysis
                    quote screen opened this tab with ?need=N. */}
                {needCredits > 0 && (
                  <p style={{ fontFamily: META, fontSize: 13, color: '#7c5200', background: '#fdf6e6', border: '1px solid #7c520033', borderRadius: 5, padding: '9px 12px', marginTop: 0, marginBottom: 12 }}>
                    Your analysis run needs <strong>{needCredits} more credit{needCredits === 1 ? '' : 's'}</strong> — the
                    smallest pack load that covers it is pre-selected below. After checkout, return to the analysis
                    tab and hit "Refresh balance."
                  </p>
                )}
                {/* pkg95 — quick picks (flat $1/credit; the stepper below
                    stays for fine-tuning) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  <span style={{ fontFamily: META, fontSize: 13, color: '#555' }}>Quick picks</span>
                  {PRESET_PACKS.map((p) => (
                    <button key={p} type="button" className="btn" disabled={payBusy}
                      onClick={() => setQty(clampQty(p))}
                      style={{
                        padding: '6px 14px', fontSize: 13,
                        border: qty === p ? '2px solid #0F1838' : '1px solid #c9cfdd',
                        fontWeight: qty === p ? 700 : 400,
                      }}>
                      {p * packCredits} credits — ${((p * packPriceCents) / 100).toFixed(0)}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: META, fontSize: 13, color: '#555' }}>Packs</span>
                  <button type="button" className="btn" aria-label="fewer packs"
                    style={{ width: 36, padding: '6px 0' }} disabled={payBusy || qty <= 1}
                    onClick={() => setQty(q => clampQty(q - 1))}>−</button>
                  <span style={{ fontSize: 18, fontWeight: 600, minWidth: 26, textAlign: 'center' }}>{qty}</span>
                  <button type="button" className="btn" aria-label="more packs"
                    style={{ width: 36, padding: '6px 0' }} disabled={payBusy || qty >= MAX_PACKS}
                    onClick={() => setQty(q => clampQty(q + 1))}>+</button>
                  <span style={{ fontFamily: META, fontSize: 13, color: '#555' }}>
                    {qty} {packWord} · {totalCredits} credits · <strong style={{ color: '#0F1838' }}>${totalDollars}</strong>
                  </span>
                  <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto' }}
                    disabled={payBusy} onClick={buyCredits}>
                    {payBusy ? <><span className="spinner" />Opening checkout…</> : 'Buy credits'}
                  </button>
                </div>
                <p style={{ fontFamily: META, fontSize: 12, color: '#888', marginTop: 10, marginBottom: 0 }}>
                  ${(packPriceCents / 100).toFixed(2)} for {packCredits} credits per pack, up to {MAX_PACKS} packs per
                  purchase — one card charge for the whole load. Credits never expire and are non-refundable.
                </p>
              </>
            )}
          </div>
          </>
          )}

          {/* History */}
          <div className="section-head">Transaction history</div>
          <div className="card">
            {ledger === null && <p style={{ fontFamily: META, fontSize: 13, color: '#888', margin: 0 }}>Loading history…</p>}
            {ledger !== null && ledger.length === 0 && (
              <p style={{ fontFamily: META, fontSize: 13, color: '#888', margin: 0 }}>
                No transactions yet — free actions and purchases will show up here.
              </p>
            )}
            {ledger !== null && ledger.length > 0 && (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: META, fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#888' }}>
                      <th style={{ padding: '4px 8px 8px 0', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '4px 8px 8px 0', fontWeight: 600 }}>Activity</th>
                      <th style={{ padding: '4px 0 8px 0', fontWeight: 600 }}>By</th>
                      <th style={{ padding: '4px 0 8px 0', fontWeight: 600, textAlign: 'right' }}>Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map(e => (
                      <tr key={e.id} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: '8px 8px 8px 0', color: '#555', whiteSpace: 'nowrap' }}>{fmtDay(e.created_at)}</td>
                        <td style={{ padding: '8px 8px 8px 0' }}>{activityLine(e)}</td>
                        <td style={{ padding: '8px 0', color: '#555' }}>{e.created_by_name || '—'}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600,
                          color: e.delta > 0 ? '#1d7a4f' : (e.delta < 0 ? '#0F1838' : '#999') }}>
                          {e.delta > 0 ? `+${e.delta}` : e.delta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontFamily: META, fontSize: 12, color: '#888', marginTop: 10, marginBottom: 0 }}>
                  Formal receipts and invoices are in the Stripe billing portal — "Manage billing" on Firm Billing.
                </p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
